import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/button";
import { Icon } from "@/components/icon/Icon";
import { useFileUpload } from "@/contexts/FileUploadContext";
import { JobList } from "@/components/Jobs/JobList";
import { HomeTabs, type HomeTab } from "@/components/Jobs/HomeTabs";
import { useEnrichedJobs } from "@/hooks/useEnrichedJobs";
import { useSharedJobs } from "@/hooks/useSharedJobs";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";
import { CreateSummaryDialog } from "@/components/CreateSummaryDialog";
import { Job, JobStatus } from "@/hooks/useJobs";
import { useJob } from "@/hooks/useJob";
import { useDownloadJob } from "@/hooks/useDownloadJob";
import { useLayout } from "@/contexts/LayoutContext";
import { MANUSCRIPT_ACCEPT_MAP } from "@/shared/constants";

type HomeQueryParams = {
  redirect: string | undefined;
  downloadJobId: string | undefined;
};

const HOME_TAB_STORAGE_KEY = "essence:homeTab";

function readStoredHomeTab(): HomeTab {
  const stored = window.sessionStorage.getItem(HOME_TAB_STORAGE_KEY);
  return stored === "shared" ? "shared" : "owned";
}

function writeStoredHomeTab(tab: HomeTab) {
  window.sessionStorage.setItem(HOME_TAB_STORAGE_KEY, tab);
}

export const Route = createFileRoute("/home")({
  component: HomeComponent,
  validateSearch: (search: Record<string, unknown>): HomeQueryParams => ({
    redirect:
      typeof search?.redirect === "string" ? search?.redirect : undefined,
    downloadJobId:
      typeof search?.downloadJobId === "string"
        ? search?.downloadJobId
        : undefined,
  }),
});

export function HomeComponent() {
  const { t } = useTranslation(["home"]);
  const { isUploading } = useFileUpload();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTabState] = useState<HomeTab>(readStoredHomeTab);
  const setActiveTab = useCallback((tab: HomeTab) => {
    setActiveTabState(tab);
    writeStoredHomeTab(tab);
  }, []);
  const {
    jobs: ownedJobs,
    isLoading: isLoadingOwnedJobs,
    hasMore,
    loadMore,
  } = useEnrichedJobs();
  const lastJob = ownedJobs[0];
  const { jobs: sharedJobs } = useSharedJobs();
  const { features } = useFeatureFlags();
  const { downloadJobId } = Route.useSearch();
  const { job: downloadJobFallback } = useJob(downloadJobId ?? null);
  const navigate = useNavigate({ from: Route.fullPath });
  const { handleDownload } = useDownloadJob();
  const { navbarHeightRem } = useLayout();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const triggeredDownloadRef = useRef<string | null>(null);

  const showTabs = features.manuscriptSharing && sharedJobs.length > 0;
  const showSharedList = showTabs && activeTab === "shared";
  const hasAnyJobs = ownedJobs.length > 0 || sharedJobs.length > 0;
  const unopenedCount = sharedJobs.filter((job) => !job.hasOpened).length;
  const showLoadMoreSentinel = !showSharedList && hasMore;

  useEffect(() => {
    if (!downloadJobId) {
      triggeredDownloadRef.current = null;
      return;
    }
    if (triggeredDownloadRef.current === downloadJobId) return;
    const fallbackJob =
      downloadJobFallback?.jobId === downloadJobId ? downloadJobFallback : null;
    const jobToDownload =
      ownedJobs.find((job) => job.jobId === downloadJobId) ??
      (isLoadingOwnedJobs ? null : fallbackJob);
    if (jobToDownload?.status === JobStatus.COMPLETED) {
      triggeredDownloadRef.current = downloadJobId;
      handleDownload(jobToDownload as Job);
      navigate({
        search: (prev) => ({
          redirect: prev.redirect,
          downloadJobId: undefined,
        }),
      });
    }
  }, [
    downloadJobId,
    ownedJobs,
    isLoadingOwnedJobs,
    downloadJobFallback,
    navigate,
    handleDownload,
  ]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!showLoadMoreSentinel || !node) return;
    if (ownedJobs.length === 0) {
      loadMore();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMore();
        }
      },
      { rootMargin: "400px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [showLoadMoreSentinel, ownedJobs, loadMore]);

  return (
    <div className="relative flex min-h-screen w-full flex-col">
      <main
        className={`flex w-full flex-col items-center gap-[0.75rem] text-center ${
          hasAnyJobs ? "" : "flex-grow justify-center"
        }`}
        style={{
          paddingTop: hasAnyJobs ? `${navbarHeightRem + 0.5}rem` : "0",
        }}
      >
        <div className="flex flex-col items-center justify-center gap-[0.75rem] text-center py-[2rem]">
          <h1 className="text-4xl font-semibold leading-10 tracking-wide">
            {t("home:title")}
          </h1>
          <p className="text-muted-foreground">{t("home:subtitle")}</p>
          <Button
            variant="default"
            className="mt-[0.5rem]"
            disabled={isUploading}
            icon={
              <Icon name="sparkles" className="foreground h-[1rem] w-[1rem]" />
            }
            onClick={() => setIsDialogOpen(true)}
          >
            {t("home:button.mainCtaLabel")}
          </Button>
          <p className="text-xs text-muted-foreground">
            {t("home:fileUpload.constraintsText")}
          </p>
        </div>
        {showTabs && (
          <div className="flex w-full justify-start pb-2">
            <HomeTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              unopenedCount={unopenedCount}
            />
          </div>
        )}
        {showSharedList
          ? sharedJobs.length > 0 && (
              <JobList variant="shared" jobs={sharedJobs} />
            )
          : ownedJobs.length > 0 && <JobList jobs={ownedJobs} />}
        {showLoadMoreSentinel && (
          <div ref={loadMoreRef} data-testid="load-more-sentinel" />
        )}
      </main>
      <CreateSummaryDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        defaultWordCount={lastJob?.desiredWordCount}
        defaultLanguage={lastJob?.outputLanguage}
        maxSizeMB={100}
        accept={MANUSCRIPT_ACCEPT_MAP}
      />
    </div>
  );
}
